import { bool, fail, isRecord, list, num, optionalText, sameResource, text } from '../../../shared/api/onecEnvelope'
import {
  MATCH_STATUSES,
  SUPPLY_STATUSES,
  type AllocationFillResult,
  type AllocationMatrix,
  type AllocationProblem,
  type AuditEntry,
  type CreatedSupply,
  type CursorPage,
  type FillResult,
  type LineAllocation,
  type LinePrice,
  type MatchStatus,
  type OneCReceipt,
  type OneCRef,
  type PostingAttempt,
  type PostingPreview,
  type PostingProgress,
  type PriceFactorResult,
  type PriceTypesDirectory,
  type StartedPosting,
  type Supply,
  type SupplyLine,
  type SupplyListItem,
  type SupplyStatus,
} from '../model/types'

/**
 * Декодеры wire-DTO поставок (B3b `SupplyContracts.cs`, B3c — по
 * business-data-v1.schema.json). Неожиданная форма → `invalid-response`, а не
 * пустой экран. Каждая `ResourceRef` в ответе проверяется на rid запроса:
 * ссылка другой базы — ошибка, а не склад «с тем же ключом» (D-064).
 */
export const ONEC_SUPPLY_SCHEMAS = {
  create: 'onec.business.Supplies_Create.v1',
  update: 'onec.business.Supplies_Update.v1',
  delete: 'onec.business.Supplies_Delete.v1',
  clone: 'onec.business.Supplies_Clone.v1',
  importInvoice: 'onec.business.Supplies_ImportInvoice.v1',
  addLine: 'onec.business.Supplies_AddLine.v1',
  updateLine: 'onec.business.Supplies_UpdateLine.v1',
  deleteLine: 'onec.business.Supplies_DeleteLine.v1',
  setIgnored: 'onec.business.Supplies_SetLineIgnored.v1',
  match: 'onec.business.Supplies_Match.v1',
  overrideMatch: 'onec.business.Supplies_OverrideLineMatch.v1',
  resetMatch: 'onec.business.Supplies_ResetLineMatch.v1',
  markNew: 'onec.business.Supplies_MarkLineAsNew.v1',
  priceFactor: 'onec.business.Supplies_ApplyPriceFactor.v1',
  setPrices: 'onec.business.Supplies_SetLinePrices.v1',
  setLineAllocations: 'onec.business.Supplies_SetLineAllocations.v1',
  defaultWarehouse: 'onec.business.Supplies_SetDefaultWarehouse.v1',
  fillAllToOne: 'onec.business.Supplies_AllocateAllToOne.v1',
  fillEvenly: 'onec.business.Supplies_SplitEvenly.v1',
  fillClear: 'onec.business.Supplies_ClearAllocations.v1',
  post: 'onec.business.Supplies_Post.v1',
} as const

const optionalNum = (value: unknown, field: string): number | null =>
  value === undefined || value === null ? null : num(value, field)

const strings = (value: unknown, field: string): string[] => list(value, field, (item, index) => text(item, `${field}[${index}]`))

const oneOf = <T extends string>(allowed: readonly T[], value: unknown, field: string): T => {
  const raw = text(value, field)
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : fail(`поле ${field}: неизвестное значение «${raw}»`)
}

/** Ссылка 1С из ответа: только своей базы. */
export function refDecoder(resourceId: string) {
  return (value: unknown, field: string): OneCRef => {
    if (!isRecord(value)) fail(`поле ${field} — не ссылка`)
    const owner = value.resourceId
    const rid = isRecord(owner) ? text(owner.value, `${field}.resourceId.value`) : text(owner, `${field}.resourceId`)
    if (!sameResource(rid, resourceId)) fail(`ссылка ${field} относится к другой базе`)
    return { remoteKey: text(value.remoteKey, `${field}.remoteKey`).toLowerCase() }
  }
}

const optionalRef = (ref: ReturnType<typeof refDecoder>, value: unknown, field: string): OneCRef | null =>
  value === undefined || value === null ? null : ref(value, field)

function cursorPage<T>(raw: unknown, item: (value: unknown, index: number) => T): CursorPage<T> {
  if (!isRecord(raw)) fail('страница — не объект')
  const complete = bool(raw.complete, 'complete')
  const nextCursor = optionalText(raw.nextCursor, 'nextCursor')
  if (!complete && nextCursor === null) fail('незавершённая страница без курсора')
  return { items: list(raw.items, 'items', item), nextCursor: complete ? null : nextCursor, complete, observedAt: text(raw.observedAt, 'observedAt') }
}

/** Ответ о поставке обязан быть про ЭТУ базу: `resourceId` поставки, если есть, сверяется с rid окна. */
function sameSupplyResource(resourceId: string, raw: Record<string, unknown>) {
  if (raw.resourceId !== undefined && raw.resourceId !== null && !sameResource(text(raw.resourceId, 'resourceId'), resourceId)) {
    fail('поставка относится к другой базе')
  }
}

export const decodePriceTypes =
  (resourceId: string) =>
  (raw: unknown): PriceTypesDirectory => {
    const ref = refDecoder(resourceId)
    if (!isRecord(raw)) fail('виды цен — не объект')
    return {
      items: list(raw.items, 'items', (item, index) => {
        if (!isRecord(item)) fail(`вид цены ${index} — не объект`)
        return { ref: ref(item.ref, `items[${index}].ref`), name: text(item.name, 'name') }
      }),
      lastMessage: optionalText(raw.lastMessage, 'lastMessage'),
      isReady: bool(raw.isReady, 'isReady'),
    }
  }

const decodeListItem =
  (resourceId: string) =>
  (raw: unknown): SupplyListItem => {
    if (!isRecord(raw)) fail('поставка — не объект')
    sameSupplyResource(resourceId, raw)
    return {
      id: text(raw.id, 'id'),
      number: text(raw.number, 'number'),
      status: oneOf<SupplyStatus>(SUPPLY_STATUSES, raw.status, 'status'),
      statusName: text(raw.statusName, 'statusName'),
      documentDate: text(raw.documentDate, 'documentDate'),
      supplierName: text(raw.supplierName, 'supplierName'),
      supplierInvoiceNumber: optionalText(raw.supplierInvoiceNumber, 'supplierInvoiceNumber'),
      warehouseName: text(raw.warehouseName, 'warehouseName'),
      linesCount: num(raw.linesCount, 'linesCount'),
      amountTotal: num(raw.amountTotal, 'amountTotal'),
      updatedAt: text(raw.updatedAt, 'updatedAt'),
      postedAt: optionalText(raw.postedAt, 'postedAt'),
      withoutSupplier: bool(raw.withoutSupplier, 'withoutSupplier'),
    }
  }

export const decodeSupplyList = (resourceId: string) => (raw: unknown) => cursorPage(raw, decodeListItem(resourceId))

export const decodeSupply =
  (resourceId: string) =>
  (raw: unknown): Supply => {
    const ref = refDecoder(resourceId)
    if (!isRecord(raw)) fail('поставка — не объект')
    sameSupplyResource(resourceId, raw)
    const summary = raw.summary
    if (!isRecord(summary)) fail('поле summary — не объект')
    return {
      id: text(raw.id, 'id'),
      number: text(raw.number, 'number'),
      status: oneOf<SupplyStatus>(SUPPLY_STATUSES, raw.status, 'status'),
      statusName: text(raw.statusName, 'statusName'),
      supplierRef: optionalRef(ref, raw.supplierRef, 'supplierRef'),
      supplierName: text(raw.supplierName, 'supplierName'),
      supplierInvoiceNumber: optionalText(raw.supplierInvoiceNumber, 'supplierInvoiceNumber'),
      withoutSupplier: bool(raw.withoutSupplier, 'withoutSupplier'),
      documentDate: text(raw.documentDate, 'documentDate'),
      warehouseRef: optionalRef(ref, raw.warehouseRef, 'warehouseRef'),
      warehouseName: text(raw.warehouseName, 'warehouseName'),
      comment: optionalText(raw.comment, 'comment'),
      importedFileId: optionalText(raw.importedFileId, 'importedFileId'),
      allowUnallocatedRemainder: bool(raw.allowUnallocatedRemainder, 'allowUnallocatedRemainder'),
      postingLockedBy: optionalText(raw.postingLockedBy, 'postingLockedBy'),
      postedAt: optionalText(raw.postedAt, 'postedAt'),
      lastPostingAttempt: num(raw.lastPostingAttempt, 'lastPostingAttempt'),
      updatedAt: text(raw.updatedAt, 'updatedAt'),
      updatedBy: optionalText(raw.updatedBy, 'updatedBy'),
      version: num(raw.version, 'version'),
      summary: {
        linesTotal: num(summary.linesTotal, 'summary.linesTotal'),
        linesMatched: num(summary.linesMatched, 'summary.linesMatched'),
        linesNew: num(summary.linesNew, 'summary.linesNew'),
        linesAmbiguous: num(summary.linesAmbiguous, 'summary.linesAmbiguous'),
        linesNotMatched: num(summary.linesNotMatched, 'summary.linesNotMatched'),
        linesIgnored: num(summary.linesIgnored, 'summary.linesIgnored'),
        quantityTotal: num(summary.quantityTotal, 'summary.quantityTotal'),
        quantityAllocated: num(summary.quantityAllocated, 'summary.quantityAllocated'),
        quantityUnallocated: num(summary.quantityUnallocated, 'summary.quantityUnallocated'),
        amountTotal: num(summary.amountTotal, 'summary.amountTotal'),
        documentsToCreate: num(summary.documentsToCreate, 'summary.documentsToCreate'),
        blockers: strings(summary.blockers, 'summary.blockers'),
      },
    }
  }

export const decodeLine =
  (resourceId: string) =>
  (raw: unknown): SupplyLine => {
    const ref = refDecoder(resourceId)
    if (!isRecord(raw)) fail('строка — не объект')
    return {
      id: text(raw.id, 'id'),
      lineNumber: num(raw.lineNumber, 'lineNumber'),
      rawName: optionalText(raw.rawName, 'rawName'),
      rawArticle: optionalText(raw.rawArticle, 'rawArticle'),
      rawBarcode: optionalText(raw.rawBarcode, 'rawBarcode'),
      quantity: num(raw.quantity, 'quantity'),
      purchasePrice: num(raw.purchasePrice, 'purchasePrice'),
      amount: num(raw.amount, 'amount'),
      unitName: optionalText(raw.unitName, 'unitName'),
      matchStatus: oneOf<MatchStatus>(MATCH_STATUSES, raw.matchStatus, 'matchStatus'),
      productId: optionalText(raw.productId, 'productId'),
      matchedBy: optionalText(raw.matchedBy, 'matchedBy'),
      matchSource: text(raw.matchSource, 'matchSource'),
      oneCName: optionalText(raw.oneCName, 'oneCName'),
      createInResource: bool(raw.createInResource, 'createInResource'),
      isIgnored: bool(raw.isIgnored, 'isIgnored'),
      notes: optionalText(raw.notes, 'notes'),
      version: num(raw.version, 'version'),
      allocations: list(raw.allocations, 'allocations', (item, index): LineAllocation => {
        if (!isRecord(item)) fail(`доля ${index} — не объект`)
        return {
          warehouseRef: optionalRef(ref, item.warehouseRef, `allocations[${index}].warehouseRef`),
          warehouseName: text(item.warehouseName, 'warehouseName'),
          quantity: num(item.quantity, 'quantity'),
        }
      }),
      prices: list(raw.prices, 'prices', (item, index): LinePrice => {
        if (!isRecord(item)) fail(`цена ${index} — не объект`)
        return {
          priceTypeRef: optionalRef(ref, item.priceTypeRef, `prices[${index}].priceTypeRef`),
          priceTypeName: text(item.priceTypeName, 'priceTypeName'),
          currentPrice: optionalNum(item.currentPrice, 'currentPrice'),
          newPrice: optionalNum(item.newPrice, 'newPrice'),
          isManualOverride: bool(item.isManualOverride, 'isManualOverride'),
        }
      }),
      candidates: list(raw.matchCandidates, 'matchCandidates', (item, index) => {
        if (!isRecord(item)) fail(`кандидат ${index} — не объект`)
        return { productId: text(item.productId, 'productId'), name: text(item.name, 'name'), score: num(item.score, 'score') }
      }),
      allocatedQuantity: num(raw.allocatedQuantity, 'allocatedQuantity'),
      isFullyAllocated: bool(raw.isFullyAllocated, 'isFullyAllocated'),
    }
  }

export const decodeLines = (resourceId: string) => (raw: unknown) => cursorPage(raw, decodeLine(resourceId))

export const decodeAllocations =
  (resourceId: string) =>
  (raw: unknown): AllocationMatrix => {
    const ref = refDecoder(resourceId)
    if (!isRecord(raw) || !isRecord(raw.rows)) fail('матрица распределения — не объект')
    return {
      warehouses: list(raw.warehouses, 'warehouses', (item, index) => {
        if (!isRecord(item)) fail(`склад ${index} — не объект`)
        return {
          ref: ref(item.ref, `warehouses[${index}].ref`),
          name: text(item.name, 'name'),
          organizationName: text(item.organizationName, 'organizationName'),
          isDefault: bool(item.isDefault, 'isDefault'),
          isMain: bool(item.isMain, 'isMain'),
          quantityTotal: num(item.quantityTotal, 'quantityTotal'),
        }
      }),
      rows: list(raw.rows.items, 'rows.items', (item, index) => {
        if (!isRecord(item)) fail(`строка ${index} — не объект`)
        const cells: Record<string, number> = {}
        for (const [cellIndex, cell] of list(item.cells, 'cells', (value) => value).entries()) {
          if (!isRecord(cell)) fail(`ячейка ${cellIndex} — не объект`)
          cells[ref(cell.warehouseRef, `cells[${cellIndex}].warehouseRef`).remoteKey] = num(cell.quantity, 'quantity')
        }
        return {
          lineId: text(item.lineId, 'lineId'),
          lineNumber: num(item.lineNumber, 'lineNumber'),
          name: optionalText(item.name, 'name'),
          quantity: num(item.quantity, 'quantity'),
          allocated: num(item.allocated, 'allocated'),
          unallocated: num(item.unallocated, 'unallocated'),
          isIgnored: bool(item.isIgnored, 'isIgnored'),
          version: num(item.version, 'version'),
          cells,
        }
      }),
      unallocatedTotal: num(raw.unallocatedTotal, 'unallocatedTotal'),
      documentsToCreate: num(raw.documentsToCreate, 'documentsToCreate'),
    }
  }

const decodeProblem = (raw: unknown, index: number): AllocationProblem => {
  if (!isRecord(raw)) fail(`проблема ${index} — не объект`)
  return {
    kind: text(raw.kind, 'kind'),
    lineNumber: num(raw.lineNumber, 'lineNumber'),
    lineName: optionalText(raw.lineName, 'lineName'),
    message: text(raw.message, 'message'),
  }
}

export const decodeProblems = (raw: unknown): AllocationProblem[] => list(raw, 'problems', decodeProblem)

export const decodePreview =
  (resourceId: string) =>
  (raw: unknown): PostingPreview => {
    const ref = refDecoder(resourceId)
    if (!isRecord(raw)) fail('предпросмотр — не объект')
    return {
      documents: list(raw.documents, 'documents', (item, index) => {
        if (!isRecord(item)) fail(`документ ${index} — не объект`)
        ref(item.warehouseRef, `documents[${index}].warehouseRef`)
        return {
          kind: text(item.kind, 'kind'),
          warehouseName: text(item.warehouseName, 'warehouseName'),
          organizationName: text(item.organizationName, 'organizationName'),
          sourceWarehouseName: optionalText(item.sourceWarehouseName, 'sourceWarehouseName'),
          linesCount: num(item.linesCount, 'linesCount'),
          quantityTotal: num(item.quantityTotal, 'quantityTotal'),
          amountTotal: num(item.amountTotal, 'amountTotal'),
        }
      }),
      pricesToWrite: num(raw.pricesToWrite, 'pricesToWrite'),
      warnings: strings(raw.warnings, 'warnings'),
      problems: decodeProblems(raw.problems),
      resourceBlockers: strings(raw.resourceBlockers, 'resourceBlockers'),
      canPost: bool(raw.canPost, 'canPost'),
      previewHash: text(raw.previewHash, 'previewHash'),
      headline: text(raw.headline, 'headline'),
    }
  }

export const decodeProgress = (supplyId: string) => (raw: unknown): PostingProgress => {
  if (!isRecord(raw)) fail('статус проведения — не объект')
  if (text(raw.supplyId, 'supplyId').toLowerCase() !== supplyId.toLowerCase()) fail('статус проведения другой поставки')
  return {
    attemptNumber: num(raw.attemptNumber, 'attemptNumber'),
    isRunning: bool(raw.isRunning, 'isRunning'),
    totalSteps: num(raw.totalSteps, 'totalSteps'),
    completedSteps: num(raw.completedSteps, 'completedSteps'),
    failedSteps: num(raw.failedSteps, 'failedSteps'),
    skippedSteps: num(raw.skippedSteps, 'skippedSteps'),
    currentStepKind: optionalText(raw.currentStepKind, 'currentStepKind'),
    entries: list(raw.entries, 'entries', (item, index) => {
      if (!isRecord(item)) fail(`шаг ${index} — не объект`)
      return {
        id: text(item.id, 'id'),
        stepKind: text(item.stepKind, 'stepKind'),
        status: text(item.status, 'status'),
        resourceEntityName: optionalText(item.resourceEntityName, 'resourceEntityName'),
        httpStatus: optionalNum(item.httpStatus, 'httpStatus'),
        resourceErrorText: optionalText(item.resourceErrorText, 'resourceErrorText'),
        startedAt: text(item.startedAt, 'startedAt'),
        correlationId: optionalText(item.correlationId, 'correlationId'),
      }
    }),
    documents: list(raw.documents, 'documents', (item, index) => {
      if (!isRecord(item)) fail(`документ ${index} — не объект`)
      return {
        kind: text(item.kind, 'kind'),
        warehouseName: text(item.warehouseName, 'warehouseName'),
        sourceWarehouseName: optionalText(item.sourceWarehouseName, 'sourceWarehouseName'),
        state: text(item.state, 'state'),
        documentNumber: optionalText(item.documentNumber, 'documentNumber'),
        resourceErrorText: optionalText(item.resourceErrorText, 'resourceErrorText'),
      }
    }),
    finalStatus: optionalText(raw.finalStatus, 'finalStatus'),
  }
}

export const decodePostingLog = (raw: unknown): PostingAttempt[] =>
  list(raw, 'attempts', (item, index) => {
    if (!isRecord(item)) fail(`попытка ${index} — не объект`)
    return {
      attemptNumber: num(item.attemptNumber, 'attemptNumber'),
      startedAt: text(item.startedAt, 'startedAt'),
      finishedAt: optionalText(item.finishedAt, 'finishedAt'),
      total: num(item.total, 'total'),
      succeeded: num(item.succeeded, 'succeeded'),
      failed: num(item.failed, 'failed'),
      skipped: num(item.skipped, 'skipped'),
      steps: list(item.steps, 'steps', (step, stepIndex) => {
        if (!isRecord(step)) fail(`шаг ${stepIndex} — не объект`)
        return {
          stepKind: text(step.stepKind, 'stepKind'),
          stepKindName: text(step.stepKindName, 'stepKindName'),
          status: text(step.status, 'status'),
          statusName: text(step.statusName, 'statusName'),
          documentNumber: optionalText(step.documentNumber, 'documentNumber'),
          resourceErrorText: optionalText(step.resourceErrorText, 'resourceErrorText'),
        }
      }),
    }
  })

const decodeAudit = (raw: unknown, index: number): AuditEntry => {
  if (!isRecord(raw)) fail(`запись истории ${index} — не объект`)
  const id = raw.id
  return {
    id: typeof id === 'number' ? String(id) : text(id, 'id'),
    occurredAt: text(raw.occurredAt, 'occurredAt'),
    actionName: text(raw.actionName, 'actionName'),
    lineNumber: optionalNum(raw.lineNumber, 'lineNumber'),
    fieldName: optionalText(raw.fieldName, 'fieldName'),
    oldValue: optionalText(raw.oldValue, 'oldValue'),
    newValue: optionalText(raw.newValue, 'newValue'),
    comment: optionalText(raw.comment, 'comment'),
    userName: optionalText(raw.userName, 'userName'),
    sourceName: text(raw.sourceName, 'sourceName'),
  }
}

export const decodeHistory = (raw: unknown) => cursorPage(raw, decodeAudit)
export const decodeFieldHistory = (raw: unknown): AuditEntry[] => list(raw, 'history', decodeAudit)

export const decodeReceipts = (raw: unknown) =>
  cursorPage(raw, (item, index): OneCReceipt => {
    if (!isRecord(item)) fail(`приходная ${index} — не объект`)
    return {
      refKey: text(item.refKey, 'refKey'),
      number: optionalText(item.number, 'number'),
      date: optionalText(item.date, 'date'),
      posted: bool(item.posted, 'posted'),
      supplierName: text(item.supplierName, 'supplierName'),
      warehouseName: text(item.warehouseName, 'warehouseName'),
      sum: num(item.sum, 'sum'),
      linesCount: list(item.lines, 'lines', (value) => value).length,
    }
  })

// --- ответы команд -------------------------------------------------------------------------

export const decodeCreated = (raw: unknown): CreatedSupply => {
  if (!isRecord(raw)) fail('ответ создания — не объект')
  return { id: text(raw.id, 'id'), number: text(raw.number, 'number') }
}

/**
 * Ответ правки: предупреждения и новая версия строки, если служба их дала
 * (`LineEditResultDto`, `BulkUpdateResultDto`…). Пустой ответ (204/`null`) — без них.
 */
export const decodeDone = (raw: unknown): { warnings: string[]; lineVersion: number | null } => {
  if (!isRecord(raw)) return { warnings: [], lineVersion: null }
  const warnings = raw.warnings === undefined ? [] : strings(raw.warnings, 'warnings')
  const line = raw.line
  return { warnings, lineVersion: isRecord(line) ? num(line.version, 'line.version') : null }
}

export const decodeFill = (raw: unknown): FillResult => {
  if (!isRecord(raw)) fail('итог заполнения — не объект')
  return {
    fileName: text(raw.fileName, 'fileName'),
    totalRows: num(raw.totalRows, 'totalRows'),
    added: num(raw.added, 'added'),
    updated: num(raw.updated, 'updated'),
    skippedExisting: num(raw.skippedExisting, 'skippedExisting'),
    removed: num(raw.removed, 'removed'),
    skipped: list(raw.skipped, 'skipped', (item, index) => {
      if (!isRecord(item)) fail(`пропуск ${index} — не объект`)
      return {
        sourceRowNumber: num(item.sourceRowNumber, 'sourceRowNumber'),
        name: optionalText(item.name, 'name'),
        reason: text(item.reason, 'reason'),
      }
    }),
  }
}

export const decodeFactor = (raw: unknown): PriceFactorResult => {
  if (!isRecord(raw)) fail('итог коэффициента — не объект')
  return {
    applied: bool(raw.applied, 'applied'),
    linesAffected: num(raw.linesAffected, 'linesAffected'),
    linesSkipped: num(raw.linesSkipped, 'linesSkipped'),
    oldAmountTotal: num(raw.oldAmountTotal, 'oldAmountTotal'),
    newAmountTotal: num(raw.newAmountTotal, 'newAmountTotal'),
  }
}

/** Итог fill-команды распределения (`AllocationFillPreviewDto`); строки «до/после» экрану не нужны — он перечитывает матрицу. */
export const decodeAllocationFill = (raw: unknown): AllocationFillResult => {
  if (!isRecord(raw)) fail('итог распределения — не объект')
  return {
    applied: bool(raw.applied, 'applied'),
    linesAffected: num(raw.linesAffected, 'linesAffected'),
    warnings: raw.warnings === undefined ? [] : strings(raw.warnings, 'warnings'),
  }
}

export const decodeStartedPosting = (supplyId: string) => (raw: unknown): StartedPosting => {
  if (!isRecord(raw)) fail('ответ проведения — не объект')
  if (text(raw.supplyId, 'supplyId').toLowerCase() !== supplyId.toLowerCase()) fail('ответ проведения другой поставки')
  return { attemptNumber: num(raw.attemptNumber, 'attemptNumber') }
}

/** Ссылка 1С для тела команды: rid — из адреса, ключ — из справочника этой же базы. */
export const wireRef = (resourceId: string, remoteKey: string) => ({
  resourceId: { value: resourceId, isEmpty: false },
  remoteKey,
})
